import React, { useState, useEffect } from "react";
import { useAgents } from "@/lib/useAgents";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  User,
  Building,
  Briefcase,
  UserCheck,
  AlertTriangle,
  Save,
  CheckCircle2,
  RefreshCw,
} from "lucide-react";

interface MasterLookupRecord {
  code: string;
  name: string;
}

interface AssignableUser {
  id: string;
  first_name: string;
  last_name?: string;
  email: string;
  user_role?: { name: string; code?: string };
}

export function LeadFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { hasPermission, user, getScope } = useAuth();
  // Only a Super Admin routes leads company-wide; anyone else's unassigned new lead becomes their own
  const routesCompanyWide = getScope("lead") === "all";

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [dupWarning, setDupWarning] = useState<string | null>(null);

  // Lookups and assignable users
  const [assignableReps, setAssignableReps] = useState<AssignableUser[]>([]);
  const [masterSources, setMasterSources] = useState<MasterLookupRecord[]>([]);
  const [masterIndustries, setMasterIndustries] = useState<MasterLookupRecord[]>([]);
  const [productTypes, setProductTypes] = useState<MasterLookupRecord[]>([]);
  const [units, setUnits] = useState<MasterLookupRecord[]>([]);
  const [endUses, setEndUses] = useState<MasterLookupRecord[]>([]);
  const agents = useAgents();

  // Form State
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    mobile: "",
    alternate_phone: "",
    job_title: "",
    company_name: "",
    industry: "",
    company_size: "",
    website: "",
    city: "",
    state: "",
    region: "",
    source: "WEBSITE",
    campaign: "",
    referred_by: "",
    product_interest: "",
    expected_budget: 0,
    expected_timeline: "",
    requirement_notes: "",
    product_type: "",
    required_qty: "",
    qty_unit: "",
    target_price: "",
    end_use: "",
    required_by: "",
    agent_id: "",
    priority: "WARM" as "HOT" | "WARM" | "COLD",
    owner_id: "",
    estimated_value: 0,
    next_follow_up_at: "",
  });

  // Load supporting data
  useEffect(() => {
    // 1. Assignable users
    api
      .get("/api/User/Assignable?for=lead")
      .then((res) => setAssignableReps(unwrapArray(res)))
      .catch(() => {});

    // 2. Sources
    api
      .get("/api/MasterLookup/ByCategory/LEAD_SOURCE")
      .then((res) => {
        const items = unwrapArray<MasterLookupRecord>(res);
        setMasterSources(
          items.length > 0
            ? items
            : [
                { code: "WEBSITE", name: "Inbound Website" },
                { code: "REFERRAL", name: "Client Referral" },
                { code: "COLD_CALL", name: "Outbound Calling" },
                { code: "CAMPAIGN", name: "Marketing Campaign" },
                { code: "EXHIBITION", name: "Trade Exhibition" },
                { code: "WHATSAPP", name: "WhatsApp Business" },
              ]
        );
      })
      .catch(() => {});

    // Textile requirement pick-lists
    api.get("/api/MasterLookup/ByCategory/PRODUCT_TYPE").then((res) => setProductTypes(unwrapArray<MasterLookupRecord>(res))).catch(() => {});
    api.get("/api/MasterLookup/ByCategory/PRODUCT_UNIT").then((res) => setUnits(unwrapArray<MasterLookupRecord>(res))).catch(() => {});
    api.get("/api/MasterLookup/ByCategory/END_USE").then((res) => setEndUses(unwrapArray<MasterLookupRecord>(res))).catch(() => {});

    // 3. Buyer types (INDUSTRY lookup)
    api
      .get("/api/MasterLookup/ByCategory/INDUSTRY")
      .then((res) => {
        const items = unwrapArray<MasterLookupRecord>(res);
        setMasterIndustries(
          items.length > 0
            ? items
            : [
                { code: "GARMENT_EXPORTER", name: "Garment Exporter" },
                { code: "GARMENT_MANUFACTURER", name: "Garment Manufacturer" },
                { code: "FABRIC_TRADER", name: "Fabric Trader / Wholesaler" },
                { code: "YARN_TRADER", name: "Yarn / Fibre Trader" },
                { code: "RETAIL_BRAND", name: "Retailer / Brand" },
                { code: "HOME_FURNISHING", name: "Home Furnishing / Made-ups" },
                { code: "INSTITUTIONAL", name: "Institutional / Uniforms" },
              ]
        );
      })
      .catch(() => {});
  }, []);

  // If in edit mode, fetch the lead record
  useEffect(() => {
    if (!id) return;
    setLoading(true);

    api
      .get(`/api/Lead/ById/${id}`)
      .then((res) => {
        const l = unwrapObject<any>(res);
        if (!l || !l.id) {
          error("Lead Not Found", "The requested lead could not be found");
          navigate("/leads");
          return;
        }

        setForm({
          first_name: l.first_name || "",
          last_name: l.last_name || "",
          email: l.email || "",
          phone: l.phone || "",
          mobile: l.mobile || "",
          alternate_phone: l.alternate_phone || "",
          job_title: l.job_title || "",
          company_name: l.company_name || "",
          industry: l.industry || "",
          company_size: l.company_size || "",
          website: l.website || "",
          city: l.city || "",
          state: l.state || "",
          region: l.region || "",
          source: l.source || "WEBSITE",
          campaign: l.campaign || "",
          referred_by: l.referred_by || "",
          product_interest: l.product_interest || "",
          expected_budget: Number(l.expected_budget || 0),
          expected_timeline: l.expected_timeline || "",
          requirement_notes: l.requirement_notes || "",
          product_type: l.product_type || "",
          required_qty: l.required_qty !== null && l.required_qty !== undefined ? String(Number(l.required_qty)) : "",
          qty_unit: l.qty_unit || "",
          target_price: l.target_price !== null && l.target_price !== undefined ? String(Number(l.target_price)) : "",
          end_use: l.end_use || "",
          required_by: l.required_by ? String(l.required_by).slice(0, 10) : "",
          agent_id: l.agent_id || "",
          priority: l.priority || "WARM",
          owner_id: l.owner_id || "",
          estimated_value: Number(l.estimated_value || 0),
          next_follow_up_at: l.next_follow_up_at ? l.next_follow_up_at.slice(0, 16) : "",
        });
      })
      .catch((err) => {
        error("Error Loading Lead", err.message || "Could not retrieve lead details");
        navigate("/leads");
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Duplicate detection
  const checkDuplicatesDebounced = async (email?: string, phone?: string, company?: string) => {
    if ((!email || email.length < 5) && (!phone || phone.length < 7) && (!company || company.length < 3)) {
      setDupWarning(null);
      return;
    }
    try {
      const res = await api.post("/api/Lead/CheckDuplicate", {
        email,
        phone,
        company_name: company,
        exclude_id: id || undefined,
      });
      const data = res?.data || res;
      if (data?.isDuplicate && data?.reasons?.length > 0) {
        setDupWarning(data.reasons.join(" • "));
      } else {
        setDupWarning(null);
      }
    } catch {
      setDupWarning(null);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    if (field === "email" || field === "phone" || field === "company_name") {
      checkDuplicatesDebounced(updated.email, updated.phone, updated.company_name);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.first_name.trim()) {
      error("Validation Error", "Company name and contact first name are mandatory");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || undefined,
        company_name: form.company_name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        mobile: form.mobile.trim() || undefined,
        alternate_phone: form.alternate_phone.trim() || undefined,
        job_title: form.job_title.trim() || undefined,
        industry: form.industry.trim() || undefined,
        company_size: form.company_size.trim() || undefined,
        website: form.website.trim() || undefined,
        city: form.city.trim() || undefined,
        state: form.state.trim() || undefined,
        region: form.region.trim() || undefined,
        source: form.source,
        campaign: form.campaign.trim() || undefined,
        referred_by: form.referred_by.trim() || undefined,
        product_interest: form.product_interest.trim() || undefined,
        expected_budget: Number(form.expected_budget) || 0,
        expected_timeline: form.expected_timeline.trim() || undefined,
        requirement_notes: form.requirement_notes.trim() || undefined,
        product_type: form.product_type,
        required_qty: form.required_qty === "" ? null : Number(form.required_qty),
        qty_unit: form.qty_unit,
        target_price: form.target_price === "" ? null : Number(form.target_price),
        end_use: form.end_use,
        required_by: form.required_by || null,
        agent_id: form.agent_id || null,
        priority: form.priority,
        owner_id: form.owner_id ? form.owner_id : undefined,
        estimated_value: Number(form.estimated_value) || Number(form.expected_budget) || 0,
        next_follow_up_at: form.next_follow_up_at ? new Date(form.next_follow_up_at).toISOString() : undefined,
      };

      if (isEdit && id) {
        await api.put(`/api/Lead/Update/${id}`, payload);
        success("Lead Updated", "Lead details and user assignment saved successfully");
      } else {
        await api.post("/api/Lead/Insert", payload);
        success("Lead Created", "New lead prospect created and assigned successfully");
      }

      navigate("/leads");
    } catch (err: any) {
      error(isEdit ? "Update Failed" : "Creation Failed", err.message || "Failed to save lead");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-3">
        <RefreshCw className="size-8 text-primary animate-spin" />
        <p className="text-sm text-muted-foreground font-medium">Loading lead dossier...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-16">
      {/* Top Header & Breadcrumb */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
            <Link to="/leads" className="hover:text-foreground flex items-center gap-1 transition-colors">
              <ArrowLeft className="size-3.5" />
              <span>Leads Directory</span>
            </Link>
            <span>/</span>
            <span className="text-foreground font-medium">{isEdit ? "Edit Lead" : "New Lead"}</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2.5">
            <span>{isEdit ? `Edit Lead: ${form.first_name} ${form.last_name || ""}` : "Create New Lead Prospect"}</span>
            {form.company_name && (
              <Badge variant="secondary" className="text-xs font-normal">
                {form.company_name}
              </Badge>
            )}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isEdit
              ? "Update prospect details, company context, commercial interest, and sales user assignment."
              : "Capture inquiry details, company profile, commercial requirement, and assign directly to a sales representative."}
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate("/leads")}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-sm"
          >
            {submitting ? (
              <span className="flex items-center gap-1.5">
                <RefreshCw className="size-3.5 animate-spin" />
                <span>Saving...</span>
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <Save className="size-3.5" />
                <span>{isEdit ? "Update Lead" : "Save & Assign Lead"}</span>
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Duplicate Alert Banner */}
      {dupWarning && (
        <div className="bg-amber-500/10 border border-amber-500/30 text-amber-800 dark:text-amber-300 p-3 rounded-lg text-xs flex items-center gap-3">
          <AlertTriangle className="size-4 shrink-0 text-amber-500" />
          <div className="flex-1 font-medium">{dupWarning}</div>
        </div>
      )}

      {/* Main Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* SECTION 1: CONTACT PERSON */}
        <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-sm font-bold text-foreground border-b border-border/60 pb-3">
            <User className="size-4 text-primary" />
            <span>1. Contact Person Details</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold">First Name *</Label>
              <Input
                required
                placeholder="e.g. Rajesh"
                value={form.first_name}
                onChange={(e) => handleFieldChange("first_name", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Last Name</Label>
              <Input
                placeholder="e.g. Singhania"
                value={form.last_name}
                onChange={(e) => handleFieldChange("last_name", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold">Email Address</Label>
              <Input
                type="email"
                placeholder="rajesh@company.com"
                value={form.email}
                onChange={(e) => handleFieldChange("email", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Job Title / Designation</Label>
              <Input
                placeholder="e.g. Managing Director / Procurement VP"
                value={form.job_title}
                onChange={(e) => handleFieldChange("job_title", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold">Primary Phone</Label>
              <Input
                placeholder="+91 98765 43210"
                value={form.phone}
                onChange={(e) => handleFieldChange("phone", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Mobile Phone</Label>
              <Input
                placeholder="+91 98765 43211"
                value={form.mobile}
                onChange={(e) => handleFieldChange("mobile", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Alternate Phone</Label>
              <Input
                placeholder="Office Landline / Extension"
                value={form.alternate_phone}
                onChange={(e) => handleFieldChange("alternate_phone", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
          </div>
        </div>

        {/* SECTION 2: COMPANY & LOCATION */}
        <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-sm font-bold text-foreground border-b border-border/60 pb-3">
            <Building className="size-4 text-primary" />
            <span>2. Company & Location Information</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold">Company Name *</Label>
              <Input
                required
                placeholder="e.g. Acme Textiles Pvt Ltd"
                value={form.company_name}
                onChange={(e) => handleFieldChange("company_name", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Buyer Type</Label>
              <select
                value={form.industry}
                onChange={(e) => handleFieldChange("industry", e.target.value)}
                className="w-full mt-1.5 h-10 rounded-md border border-input bg-background px-3 text-xs"
              >
                <option value="">-- Select Buyer Type --</option>
                {masterIndustries.map((ind) => (
                  <option key={ind.code} value={ind.name}>
                    {ind.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold">Company Website</Label>
              <Input
                placeholder="https://company.com"
                value={form.website}
                onChange={(e) => handleFieldChange("website", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Employee Headcount / Company Size</Label>
              <Input
                placeholder="e.g. 50-200 Employees"
                value={form.company_size}
                onChange={(e) => handleFieldChange("company_size", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold">City</Label>
              <Input
                placeholder="e.g. Mumbai"
                value={form.city}
                onChange={(e) => handleFieldChange("city", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">State / Province</Label>
              <Input
                placeholder="e.g. Maharashtra"
                value={form.state}
                onChange={(e) => handleFieldChange("state", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Region / Territory</Label>
              <Input
                placeholder="e.g. West Region"
                value={form.region}
                onChange={(e) => handleFieldChange("region", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
          </div>
        </div>

        {/* SECTION 3: COMMERCIAL REQUIREMENT & TIMELINE */}
        <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-sm font-bold text-foreground border-b border-border/60 pb-3">
            <Briefcase className="size-4 text-primary" />
            <span>3. Commercial Requirement & Timeline</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold">Article / Product of Interest</Label>
              <Input
                placeholder="e.g. 40s cotton poplin, 30s combed yarn, polo T-shirts"
                value={form.product_interest}
                onChange={(e) => handleFieldChange("product_interest", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Expected Purchase Timeline</Label>
              <Input
                placeholder="e.g. Immediate (1-2 weeks), Within 1 Month, Q3"
                value={form.expected_timeline}
                onChange={(e) => handleFieldChange("expected_timeline", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
          </div>

          <div className="rounded-lg border border-border/70 p-4 space-y-4">
            <p className="text-xs font-bold text-foreground">Textile requirement</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-semibold">Product Type</Label>
                <select
                  value={form.product_type}
                  onChange={(e) => handleFieldChange("product_type", e.target.value)}
                  className="w-full mt-1.5 h-10 rounded-md border border-input bg-background px-3 text-xs"
                >
                  <option value="">-- Select --</option>
                  {productTypes.map((t) => (
                    <option key={t.code} value={t.code}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Quantity Required</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    placeholder="5000"
                    value={form.required_qty}
                    onChange={(e) => handleFieldChange("required_qty", e.target.value)}
                    className="h-10 text-xs"
                  />
                  <select
                    value={form.qty_unit}
                    onChange={(e) => handleFieldChange("qty_unit", e.target.value)}
                    className="h-10 w-28 shrink-0 rounded-md border border-input bg-background px-2 text-xs"
                  >
                    <option value="">Unit</option>
                    {units.map((u) => (
                      <option key={u.code} value={u.code}>{u.code}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Target Price / Unit (₹)</Label>
                <Input
                  type="number"
                  min={0}
                  step="any"
                  value={form.target_price}
                  onChange={(e) => handleFieldChange("target_price", e.target.value)}
                  className="mt-1.5 h-10 text-xs"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-semibold">End Use</Label>
                <select
                  value={form.end_use}
                  onChange={(e) => handleFieldChange("end_use", e.target.value)}
                  className="w-full mt-1.5 h-10 rounded-md border border-input bg-background px-3 text-xs"
                >
                  <option value="">-- Select --</option>
                  {endUses.map((u) => (
                    <option key={u.code} value={u.code}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Required By</Label>
                <Input
                  type="date"
                  value={form.required_by}
                  onChange={(e) => handleFieldChange("required_by", e.target.value)}
                  className="mt-1.5 h-10 text-xs"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Agent / Broker</Label>
                <select
                  value={form.agent_id}
                  onChange={(e) => handleFieldChange("agent_id", e.target.value)}
                  className="w-full mt-1.5 h-10 rounded-md border border-input bg-background px-3 text-xs"
                >
                  <option value="">-- Direct (no agent) --</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}{a.city ? ` · ${a.city}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold">Expected Budget (₹)</Label>
              <Input
                type="number"
                placeholder="500000"
                value={form.expected_budget || ""}
                onChange={(e) => handleFieldChange("expected_budget", Number(e.target.value))}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Next Scheduled Follow-up</Label>
              <Input
                type="datetime-local"
                value={form.next_follow_up_at}
                onChange={(e) => handleFieldChange("next_follow_up_at", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold">Client Requirement Notes</Label>
            <textarea
              rows={4}
              placeholder="Shade, finish, packing, delivery location, sample needs, payment expectations..."
              value={form.requirement_notes}
              onChange={(e) => handleFieldChange("requirement_notes", e.target.value)}
              className="w-full mt-1.5 rounded-md border border-input bg-background p-3 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>

        {/* SECTION 4: ASSIGN LEAD & ACQUISITION SOURCE */}
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-sm font-bold text-foreground border-b border-primary/20 pb-3">
            <UserCheck className="size-4 text-primary" />
            <span>4. Assign Lead to User & Acquisition Source</span>
          </div>

          {/* Prominent Lead Assignment to User Card */}
          <div className="bg-background rounded-lg p-4 border border-border shadow-xs space-y-2">
            <Label className="text-xs font-bold text-foreground flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <UserCheck className="size-4 text-primary" />
                <span>Assign Lead to Sales Representative</span>
              </span>
              <span className="text-[11px] font-semibold text-primary">
                {form.owner_id ? "Direct Assignee Selected" : routesCompanyWide || isEdit ? "Unassigned Pool / Auto-Route" : "Assigned to You"}
              </span>
            </Label>
            <select
              value={form.owner_id}
              onChange={(e) => handleFieldChange("owner_id", e.target.value)}
              // Changing an existing lead's owner is a reassignment and needs lead.assign
              disabled={isEdit && !hasPermission("lead.assign")}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-xs font-semibold focus:ring-2 focus:ring-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option value="">{routesCompanyWide ? "-- Leave in Unassigned Pool (or Auto-Route) --" : "-- Assign to me --"}</option>
              {assignableReps.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.first_name} {r.last_name || ""} — {r.user_role?.name || "Representative"} ({r.email})
                </option>
              ))}
            </select>
            <p className="text-[11px] text-muted-foreground">
              The assigned sales user will receive this lead directly in their workflow and pipeline for outreach.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold">Priority Tier</Label>
              <select
                value={form.priority}
                onChange={(e) => handleFieldChange("priority", e.target.value as "HOT" | "WARM" | "COLD")}
                className="w-full mt-1.5 h-10 rounded-md border border-input bg-background px-3 text-xs font-semibold"
              >
                <option value="HOT">🔥 Hot Prospect (Urgent / High Priority)</option>
                <option value="WARM">⚡ Warm Prospect (Active Interest)</option>
                <option value="COLD">❄️ Cold Prospect (General Enquiry)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Acquisition Source</Label>
              <select
                value={form.source}
                onChange={(e) => handleFieldChange("source", e.target.value)}
                className="w-full mt-1.5 h-10 rounded-md border border-input bg-background px-3 text-xs"
              >
                <option value="">-- Select Source --</option>
                {masterSources.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold">Campaign Identifier</Label>
              <Input
                placeholder="e.g. Q3_Google_Search_Ads, Trade_Fair_2026"
                value={form.campaign}
                onChange={(e) => handleFieldChange("campaign", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Referred By</Label>
              <Input
                placeholder="e.g. Rajesh Singhania"
                value={form.referred_by}
                onChange={(e) => handleFieldChange("referred_by", e.target.value)}
                className="mt-1.5 h-10 text-xs"
              />
            </div>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/leads")}
            disabled={submitting}
          >
            ← Back to Leads
          </Button>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/leads")}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 shadow-sm"
            >
              {submitting ? (
                <span className="flex items-center gap-1.5">
                  <RefreshCw className="size-4 animate-spin" />
                  <span>Saving...</span>
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4" />
                  <span>{isEdit ? "Update Lead Dossier" : "Save & Assign Lead"}</span>
                </span>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
